import { ArrowBendUpLeft, X } from "@/components/ui/icons";
import { ToolbarIconButton } from "@/components/ui/toolbar-icon-button";

interface ReplyTarget {
  id: string;
  user_id: string;
  content: string;
}

interface ReplyingToPillProps {
  message: ReplyTarget;
  authorName: string;
  onClear: () => void;
}

const SNIPPET_MAX_CHARS = 80;

/**
 * Compact pill rendered above the composer when an inline reply is in
 * progress (mig 130). Click X to cancel the pending reply target —
 * resets composer back to top-level send mode.
 *
 * Design follows the established muted-affordance pattern: small
 * `text-xs` row, semantic tokens (`text-muted-foreground` / `bg-foreground/5`),
 * solar `ArrowBendUpLeft` icon (matches the hover-bar Reply button) +
 * inline `X` for dismissal.
 */
export function ReplyingToPill({
  message,
  authorName,
  onClear,
}: ReplyingToPillProps) {
  const snippet = makeSnippet(message.content);
  return (
    <div
      role="status"
      aria-label={`Replying to ${authorName}`}
      className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-foreground/5 text-xs text-muted-foreground"
    >
      <ArrowBendUpLeft size={12} className="shrink-0" />
      <span className="shrink-0">
        Replying to{" "}
        <span className="font-medium text-foreground/80">{authorName}</span>
      </span>
      {snippet && (
        <>
          <span className="shrink-0 text-muted-foreground/60">:</span>
          <span className="truncate flex-1 min-w-0">{snippet}</span>
        </>
      )}
      <ToolbarIconButton
        type="button"
        onClick={onClear}
        title="Cancel reply"
        aria-label="Cancel reply"
        className="ml-auto h-6 w-6 shrink-0 rounded-md"
      >
        <X size={12} />
      </ToolbarIconButton>
    </div>
  );
}

/** Single-line, length-capped excerpt of the source content. */
function makeSnippet(content: string): string {
  const collapsed = content.replace(/\s+/g, " ").trim();
  if (!collapsed) return "";
  if (collapsed.length <= SNIPPET_MAX_CHARS) return collapsed;
  return collapsed.slice(0, SNIPPET_MAX_CHARS - 1).trimEnd() + "…";
}
